/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable implicit-arrow-linebreak */
import React from 'react';
import { useTranslation } from 'react-i18next';
import closeSvg from 'src/assets/close.svg';
import { AlertAdapter, CommonInput, FormErrorWrap, RegularButton } from 'pg-ui-library';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import ReactModal from 'react-modal';
import * as sheetLoader from 'src/utils/spreadsheetLoader';
import * as yup from 'yup';
import { AxiosError } from 'axios';
import BenefitsMasterService from 'src/services/benefits-master.service';
import {
  CloseIcon,
  EnrollmentUploadBody,
  EnrollmentUploadContainer,
  EnrollmentUploadFileDrop,
  EnrollmentUploadFooter,
  EnrollmentUploadFormContainer,
  EnrollmentUploadHeader,
  EnrollmentUploadIcon,
  EnrollmentUploadList,
  EnrollmentUploadListContainer,
  EnrollmentUploadListRemove,
  EnrollmentUploadListTitle,
  EnrollmentUploadSubTitle,
  EnrollmentUploadSubTitleContainer,
  EnrollmentUploadTitle,
  TrashIcon,
} from './styles';

const SUPPORTED_FORMATS = ['xlsx', 'xls'];

const EnrollmentUploadModal: React.FC<any> = ({ isOpen, setIsOpen }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const validationSchema = yup.object().shape({
    name: yup.string(),
    file: yup
      .mixed()
      .required('File is required to upload')
      .test(
        'fileFormat',
        'Invalid File Format',
        value => value && SUPPORTED_FORMATS.includes(value.name.split('.').pop())
      ),
  });

  const mutation = useMutation(
    (variables: { file: any }) =>
      BenefitsMasterService.uploadBenefitsMaster({
        formData: variables.file,
      }),
    {
      onSuccess: () => {
        AlertAdapter('File Uploaded successfully', 'success');
        queryClient.invalidateQueries(['benefits']);

        setIsOpen(false);
      },
      onError: (error: AxiosError) => {
        AlertAdapter(error.response?.data.message, 'error');
      },
    }
  );

  const formik = useFormik({
    initialValues: {
      file: undefined,
      name: '',
    },
    validationSchema,
    onSubmit: async values => {
      if (values.file) {
        const formValues = await sheetLoader.load(values.file);
        const submitValues = {
          file: formValues,
        };

        await mutation.mutate(submitValues);
      } else {
        AlertAdapter(t('global.form.fileRequired'), 'error');
      }
    },
  });

  const handleModal = () => {
    setIsOpen(!isOpen);
  };

  const handleModalClose = () => {
    setIsOpen(false);
  };

  const onFileRemove = async () => {
    formik.setFieldValue('file', null);
    formik.setFieldValue('name', '');
  };

  const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length) {
      formik.setFieldValue('name', event.target?.files[0].name);
      formik.setFieldValue('file', event.target?.files[0]);
    }
  };

  return (
    <ReactModal
      isOpen={isOpen}
      ariaHideApp={false}
      style={{
        overlay: {
          margin: 'auto',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        },
        content: {
          borderRadius: '30px',
          margin: 'auto',
          maxWidth: '700px',
          minWidth: '700px',
          maxHeight: '420px',
          minHeight: '420px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          background: '#F3F4F8',
        },
      }}
      onRequestClose={handleModalClose}
    >
      <EnrollmentUploadContainer onSubmit={formik.handleSubmit}>
        <EnrollmentUploadHeader>
          <EnrollmentUploadTitle>Benefits Upload</EnrollmentUploadTitle>
          <EnrollmentUploadIcon>
            <CloseIcon src={closeSvg} onClick={() => handleModal()} />
          </EnrollmentUploadIcon>
        </EnrollmentUploadHeader>
        <EnrollmentUploadBody>
          <EnrollmentUploadSubTitleContainer>
            <EnrollmentUploadSubTitle>
              <EnrollmentUploadListContainer>
                <EnrollmentUploadListTitle>Document Name</EnrollmentUploadListTitle>
                <EnrollmentUploadList
                  style={{
                    background: '#F3F4F8',
                    display: 'flex',
                    alignSelf: 'center',
                    borderRadius: '10px',
                  }}
                >
                  <CommonInput
                    disabled
                    value={formik.values.name}
                    label=""
                    style={{ width: '550px', border: '0px black solid' }}
                  />
                  <EnrollmentUploadListRemove onClick={() => onFileRemove()}>
                    <TrashIcon />
                  </EnrollmentUploadListRemove>
                </EnrollmentUploadList>
              </EnrollmentUploadListContainer>
            </EnrollmentUploadSubTitle>
          </EnrollmentUploadSubTitleContainer>
          <EnrollmentUploadFormContainer>
            <EnrollmentUploadFooter>
              <label id="file" htmlFor="file">
                <EnrollmentUploadFileDrop
                  type="file"
                  id="file"
                  name="file"
                  multiple={false}
                  accept="xlsx, xls"
                  onChange={onFileChange}
                />
              </label>
              <FormErrorWrap visible errorMessage={formik.errors.file || ''} />
              <RegularButton
                style={{ width: '155px', height: '40px', marginTop: '0.1%' }}
                disabled={!formik.values.file || mutation.isLoading}
                type="submit"
              >
                {t('global.form.save')}
              </RegularButton>
            </EnrollmentUploadFooter>
          </EnrollmentUploadFormContainer>
        </EnrollmentUploadBody>
      </EnrollmentUploadContainer>
    </ReactModal>
  );
};

export default EnrollmentUploadModal;
